import 'yupLocale';
import {
  OidcProvider,
  useOidcUser,
  withOidcSecure
} from '@axa-fr/react-oidc';
import { CssBaseline } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import App from 'App';
import Loading from 'components/Loading';
import SessionLost from 'components/SessionLost';
import { OIDC_CONFIGURATION } from 'config';
import 'index.css';
import { publicPages } from 'modules';
import { SnackbarProvider } from 'notistack';
import {
  initAnalytics,
  PageTracker
} from 'providers/analytics';
import {
  ApiProvider,
  withQueryClientDefaults
} from 'providers/api';
import StyledMaterialDesignContent from 'providers/api/SnackbarStyles';
import { AuthorisationProvider } from 'providers/authorisation';
import { ConfirmationProvider } from 'providers/confirm';
import { PageProvider } from 'providers/page';
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  Route,
  BrowserRouter as Router,
  Routes
} from 'react-router-dom';
import theme from 'theme/theme';
import { isLabOnlyUser } from 'utils/roles';
import reportWebVitals from './reportWebVitals';

const withAuthorisation = (WrappedComponent: React.ComponentType) => () => <AuthorisationProvider><WrappedComponent /></AuthorisationProvider>;
const AppWithAuthAndApi = withOidcSecure(withQueryClientDefaults(withAuthorisation(App)));

const extractRoles = (oidcUser: any): string[] => {
  if (!oidcUser) return [];

  const profile = oidcUser.profile ?? oidcUser ?? {};
  let roles: string[] = [];

  if (Array.isArray(profile.roles)) roles = profile.roles;
  if (!roles.length && Array.isArray(profile.role)) roles = profile.role;
  if (!roles.length && typeof profile.role === 'string') roles = [profile.role];

  if (!roles.length && profile.realm_access?.roles) {
    roles = roles.concat(profile.realm_access.roles);
  }
  if (!roles.length && profile.resource_access && typeof profile.resource_access === 'object') {
    Object.values(profile.resource_access).forEach((val: any) => {
      if (val?.roles && Array.isArray(val.roles)) roles = roles.concat(val.roles);
    });
  }

  Object.keys(profile).forEach((k) => {
    const lower = k.toLowerCase();
    if (lower.includes('role') || lower.includes('roles') || lower.includes('group')) {
      const v = profile[k];
      if (Array.isArray(v)) roles = roles.concat(v);
      else if (typeof v === 'string') roles.push(v);
    }
  });

  return Array.from(new Set(roles.map((r) => String(r).toLowerCase())));
};

const RoleGate: React.FC = () => {
  const { oidcUser } = useOidcUser();

  const roles = extractRoles(oidcUser);
  const isLabOnly = isLabOnlyUser(roles);

  React.useEffect(() => {
    if (isLabOnly) {
      const target = `${window.location.origin}/app/qaqc/dashboard/active-pours`;
      if (!window.location.pathname.toLowerCase().includes('/qaqc')) {
        window.location.replace(target);
      }
    }
  }, [isLabOnly]);

  return null;
};

initAnalytics();

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <PageProvider>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <OidcProvider
            configuration={OIDC_CONFIGURATION}
            loadingComponent={Loading}
            authenticatingComponent={Loading}
            callbackSuccessComponent={Loading}
            sessionLostComponent={SessionLost}
            authenticatingErrorComponent={Loading}
          >
            <SnackbarProvider
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              Components={{
                success: StyledMaterialDesignContent,
                error: StyledMaterialDesignContent,
              }}
            >
              <ApiProvider>
                <ConfirmationProvider>
                  <Router basename="app">
                    {/* RoleGate runs BEFORE routes; when a lab user is detected we redirect to /app/qaqc */}
                    <RoleGate />
                    <PageTracker />

                    <Routes>
                      {
                        publicPages.map((page) => {
                          const NewPage = withQueryClientDefaults(page.component);
                          return (
                            <Route
                              key={page.title}
                              path={page.routeProps.path ?? ''}
                              element={<NewPage />}
                            />
                          );
                        })
                      }
                      <Route path="*" element={<AppWithAuthAndApi />} />
                    </Routes>
                  </Router>
                </ConfirmationProvider>
              </ApiProvider>
            </SnackbarProvider>
          </OidcProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </PageProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
